// docs/21 §7 intro backdrops: one painted pixel-art scene per `Scene` of the story. Each is a sky
// (gradient, stars, moon or sun), a strip of Oryx ground drawn through a kit's view-tile ruleset
// with the top row standing against the sky (the title clearing's trick), extra props from the
// world atlas, and a weather layer (rain and lightning, embers, mist, a falling star; docs/23 adds
// heat shimmer, blowing sand, a blizzard, a frost glaze and sleet). The canvas is drawn at the
// atlas's 24 px and scaled by a whole number; motion respects reduce-motion. A panel's `cast` (the
// story lexicon, core/story/lexicon.ts) re-blocks the scene to match its words: creatures and
// people on their sides, a relic on a pedestal, props, water, a road, a tower against the sky, and
// the sky and weather the text names; with no cast the Scene paints as authored.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { loadWorldAtlas, terrainFromRows, useReducedMotion } from '../../components';
import { kitFor, tileBoard } from '../../../game/tiling';
import { onBaked, paintHero, tokenLook } from '../../../game/heroKit';
import { FENCE_WOOD, FILLS, PROPS, frame as frameName } from '../../../game/tiling/oryx';
import { GROUND_KIT, type Ground, type Scene, type Stage, type StoryBiome, type StorySeason, type StoryWeather } from '../../../core/story/genesis';
import { overworldKit, type SeasonKey } from '../../../core/story/calendar';
import { blockScene, emptyCast, type Cast, type Placed } from '../../../core/story/lexicon';
import { seasonHour, type Season } from '../../../game/weather/season';

/** HEAD: rows of headroom above the horizon, where a tower stands against the sky. */
const CELL = 24, SCALE = 3, HEAD = 2;
type Prop = { frame: number; x: number; y: number; anim?: readonly number[] };
interface SceneDef { kit: string; seed: number; rows: string[]; fills?: Prop[]; props?: Prop[]; stars?: number; moon?: boolean; sun?: boolean; /** the hour on the ground, multiplied into the painted pixels only */ tint?: string; /** the cast's actors, props and relic (lexicon `blockScene`) */ placed?: Placed[] }

const W = 32;
const pad = (r: string) => (r + '.'.repeat(W)).slice(0, W);
/** A dirt road winding across the strip (row per column). */
const roadRow = (x: number) => (x < 9 ? 4 : x < 15 ? 3 : x < 23 ? 2 : 3);

const SCENES: Record<Scene, SceneDef> = {
  Night: {
    kit: 'forest', seed: 11, stars: 90, moon: true, tint: '#5f6fb8',
    rows: ['TT.TTT.TTTT..TTT.TTTTT.TT.TTTT.T', 'TTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTT', 'T.TT.....T.........TT.....TT..TT', '..,...........................,.', '................................', '......,.................,.......'],
    props: [{ frame: PROPS.well, x: 7, y: 4 }, { frame: PROPS.barrel[0], x: 9, y: 4 }, { frame: FENCE_WOOD.post, x: 4, y: 5 }, { frame: FENCE_WOOD.ew[0], x: 5, y: 5 }, { frame: FENCE_WOOD.ew[1], x: 6, y: 5 }, { frame: FENCE_WOOD.post, x: 7, y: 5 }],
  },
  Stars: {
    kit: 'forest', seed: 3, stars: 160, tint: '#6a5fb0',
    rows: ['T..TT.....T........T.....TT....T', '................................', '................................', '................................', '................................', '................................'],
    props: [{ frame: PROPS.stoneRing, x: 16, y: 3 }, { frame: PROPS.statues.hooded, x: 13, y: 2 }, { frame: PROPS.statues.hooded, x: 19, y: 2 }, { frame: PROPS.statues.knight, x: 12, y: 4 }, { frame: PROPS.statues.archer, x: 20, y: 4 }, { frame: PROPS.rocksWhite[0], x: 6, y: 4 }, { frame: PROPS.rocksWhite[2], x: 26, y: 3 }],
  },
  Storm: {
    kit: 'autumn', seed: 5, tint: '#8794a0',
    rows: ['TTTT.TTT.TTTTT.TTT.TTTTTTT.TTTTT', 'T..T...T.....T...T......T....T.T', ',,....,,,........,,,.......,,,..', '....,,.................,,.......', '................................', '..,,..........,,.........,,.....'],
    props: [{ frame: PROPS.trees.dead, x: 15, y: 3 }, { frame: PROPS.trees.dead, x: 4, y: 4 }, { frame: PROPS.trees.stump, x: 24, y: 4 }],
  },
  Ruin: {
    kit: 'forest', seed: 9, stars: 30, tint: '#a07ca8',
    rows: ['TTT..TTTT.TTT..TTTTT..TTT.TTTTTT', 'T.........,.........,.........TT', '..,...........,..........,......', '................................', '....,...............,...........', '................................'],
    props: [
      { frame: PROPS.tombstoneBroken, x: 6, y: 3 }, { frame: PROPS.tombstone, x: 8, y: 3 }, { frame: PROPS.rubble, x: 11, y: 2 }, { frame: PROPS.statues.lying, x: 14, y: 3 },
      { frame: PROPS.pedestals.figure, x: 16, y: 2 }, { frame: PROPS.paleRubble, x: 18, y: 3 }, { frame: PROPS.urns.greyBroken, x: 20, y: 4 }, { frame: PROPS.skullPile, x: 22, y: 3 },
      { frame: PROPS.rubble, x: 25, y: 4 }, { frame: PROPS.tombstoneBroken, x: 27, y: 2 }, { frame: PROPS.bones[0], x: 10, y: 4 },
    ],
  },
  Road: {
    kit: 'forest', seed: 21, tint: '#e6b8a0',
    rows: ['TTTTT.TTTT..TTTTT.TTTTTT..TTTTTT', 'TT..T.....TT.......T..TT.....T.T', '................................', '................................', '................................', '..............................,.'],
    fills: Array.from({ length: W }, (_, x) => [{ frame: FILLS.dirt, x, y: roadRow(x) }, ...(x > 0 && roadRow(x) !== roadRow(x - 1) ? [{ frame: FILLS.dirt, x, y: roadRow(x - 1) }] : [])]).flat(),
    props: [
      ...Array.from({ length: 7 }, (_, i) => ({ frame: i === 0 || i === 6 ? FENCE_WOOD.post : FENCE_WOOD.ew[i % 4]!, x: 2 + i, y: 5 })),
      { frame: PROPS.trees.dead, x: 13, y: 1 }, { frame: PROPS.rocks, x: 17, y: 3 }, { frame: PROPS.crate[0], x: 20, y: 3 }, { frame: PROPS.barrel[1], x: 21, y: 3 },
    ],
  },
  Marsh: {
    kit: 'mirewood', seed: 4, stars: 20, tint: '#7fa29c',
    rows: ['TT.T..TTT.TT..T.TTT..T.TT.TT..TT', '..~~~~...,,,..~~~~~~...,,..~~~..', '.~~~~~~~..,,.~~~~~~~~~.,,.~~~~~.', '..~~~~~...,..~~~~~~~~...,..~~~..', '.....,,,.......~~~~.....,,......', '..........,,..........,,........'],
    props: [{ frame: PROPS.trees.dead, x: 10, y: 4 }, { frame: PROPS.trees.dead, x: 24, y: 1 }],
  },
  Fire: {
    kit: 'autumn', seed: 14, tint: '#f09a70',
    rows: ['TTTT.TTTTT.TTT.TTTT.TTTTT.TTTTTT', 'T....T.......,......T.......T..T', '................................', '................................', '................................', '................................'],
    props: [
      { frame: PROPS.fire[0], x: 9, y: 2, anim: PROPS.fire }, { frame: PROPS.fire[1], x: 15, y: 3, anim: PROPS.fire }, { frame: PROPS.fire[0], x: 22, y: 2, anim: PROPS.fire },
      { frame: PROPS.brazier[0], x: 12, y: 4, anim: PROPS.brazier }, { frame: PROPS.barrel[0], x: 17, y: 4 }, { frame: PROPS.crate[1], x: 7, y: 4 }, { frame: PROPS.urns.redBroken, x: 19, y: 3 },
      { frame: PROPS.bannerRed[0], x: 25, y: 3 },
    ],
  },
  Dawn: {
    kit: 'forest', seed: 8, sun: true, tint: '#ffd8b8',
    rows: ['T.TTT.TT....TTTT..TT.....TTT.TTT', '..T.........T..........T........', '................................', '................................', '...,..................,.........', '................................'],
    props: [{ frame: PROPS.flowers[1], x: 8, y: 4 }, { frame: PROPS.flowers[2], x: 9, y: 4 }, { frame: PROPS.trees.stump, x: 20, y: 3 }],
  },
};

type Atlas = NonNullable<Awaited<ReturnType<typeof loadWorldAtlas>>>;
const sheets = new Map<string, Promise<Atlas | undefined>>();
/** The creatures or items atlas (image + frame rects), fetched once per page like the world's. */
function loadSheet(name: 'creatures' | 'items'): Promise<Atlas | undefined> {
  let p = sheets.get(name);
  if (!p) {
    p = (async () => {
      try {
        const r = await fetch(`/assets/oryx/${name}.json`);
        if (!r.ok) return undefined;
        const j = (await r.json()) as { frames: Record<string, { frame: Atlas['frames'][string] }> };
        const img = new Image();
        img.src = `/assets/oryx/${name}.png`;
        await img.decode();
        return { img, frames: Object.fromEntries(Object.entries(j.frames).map(([k, v]) => [k, v.frame])) };
      } catch { return undefined; }
    })();
    sheets.set(name, p);
  }
  return p;
}
type Sheets = { world?: Atlas; creatures?: Atlas; items?: Atlas };
const loadAll = (): Promise<Sheets> => Promise.all([loadWorldAtlas(), loadSheet('creatures'), loadSheet('items')]).then(([world, creatures, items]) => ({ world, creatures, items }));

function useSceneCanvas(def: SceneDef, onPainted?: () => void) {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();
  const rows = useMemo(() => def.rows.map(pad), [def]);
  const board = useMemo(() => terrainFromRows(rows, def.seed), [rows, def.seed]);
  const [sheets, setSheets] = useState<Sheets>();
  const atlas = sheets?.world;
  const [phase, setPhase] = useState(0);
  // a hero's kit frames bake after the first paint: paint again when they're in
  const [baked, setBaked] = useState(0);
  useEffect(() => onBaked(() => setBaked((n) => n + 1)), []);
  const painted = useRef(onPainted);
  painted.current = onPainted;
  useEffect(() => { let live = true; void loadAll().then((a) => { if (live) setSheets(a); }); return () => { live = false; }; }, []);
  useEffect(() => {
    if (reduced) return;
    const t = window.setInterval(() => setPhase((p) => 1 - p), 420);
    return () => window.clearInterval(t);
  }, [reduced]);
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx || !atlas) return;
    const tiled = tileBoard(board, kitFor(def.kit));
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const draw = (name: string, x: number, y: number, lip?: number) => {
      const f = atlas.frames[name];
      if (!f) return;
      const h = lip ?? f.h;
      ctx.drawImage(atlas.img, f.x, f.y + f.h - h, f.w, h, x * CELL, (y + HEAD) * CELL + (CELL - h), f.w, h);
    };
    /** a creature (frame A, or B on the off beat) or an item, mirrored to face left when `flip` */
    const sprite = (sheet: Atlas | undefined, name: string, x: number, y: number, flip?: boolean) => {
      const f = sheet?.frames[name];
      if (!sheet || !f) return;
      const px = Math.round(x * CELL + (CELL - f.w) / 2), py = Math.round((y + HEAD) * CELL + (CELL - f.h));
      if (!flip) { ctx.drawImage(sheet.img, f.x, f.y, f.w, f.h, px, py, f.w, f.h); return; }
      ctx.save(); ctx.translate(px + f.w, py); ctx.scale(-1, 1);
      ctx.drawImage(sheet.img, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
      ctx.restore();
    };
    for (const layer of ['ground', 'overlay', 'props'] as const) {
      for (let y = 0; y < tiled.h; y++) for (let x = 0; x < tiled.w; x++) {
        // the horizon row keeps only a grass lip under the trunks; the tree tops stand against the sky
        if (y === 0 && layer === 'overlay') continue;
        for (const v of tiled.cells[y * tiled.w + x]![layer]) draw(v.anim && phase ? v.anim[phase % v.anim.length]! : v.frame, x, y, y === 0 && layer === 'ground' ? 5 : undefined);
      }
      if (layer === 'ground') for (const p of def.fills ?? []) draw(frameName(p.frame), p.x, p.y);
    }
    for (const p of def.props ?? []) draw(frameName(p.anim && phase ? p.anim[phase % p.anim.length]! : p.frame), p.x, p.y);
    // the cast back to front, so a nearer row stands over a farther one
    for (const p of [...(def.placed ?? [])].sort((a, b) => a.y - b.y)) {
      if (p.atlas === 'world') draw(frameName(p.anim && phase ? p.anim[phase % p.anim.length]! : p.frame), p.x, p.y);
      // docs/145: a hero in the cast is a token for its kit look, breathing on the scene's beat
      else if (p.atlas === 'creature' && tokenLook(p.frame)) paintHero(ctx, tokenLook(p.frame)!, phase ? 1 : 0, p.x * CELL, (p.y + HEAD) * CELL, p.flip);
      else if (p.atlas === 'creature') sprite(sheets?.creatures, `creature_${String(p.frame + (phase ? 18 : 0)).padStart(3, '0')}`, p.x, p.y, p.flip);
      else sprite(sheets?.items, `item_${String(p.frame).padStart(3, '0')}`, p.x, p.y);
    }
    if (def.tint) {
      // multiply the hour into the painted pixels, then cut back to their alpha (the sky shows through)
      const shape = document.createElement('canvas');
      shape.width = ctx.canvas.width; shape.height = ctx.canvas.height;
      shape.getContext('2d')!.drawImage(ctx.canvas, 0, 0);
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = def.tint;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(shape, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    }
    painted.current?.();
  }, [atlas, sheets, board, def, phase, baked]);
  return { ref, w: rows[0]!.length, h: rows.length, ready: !!atlas };
}

function hash01(n: number): number {
  let t = (n + 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
const starField = (n: number, seed: number) => Array.from({ length: n }, (_, i) => {
  const a = hash01(seed * 7919 + i * 2), b = hash01(seed * 7919 + i * 2 + 1);
  return { left: a * 100, top: Math.pow(b, 1.4) * 96, size: i % 9 === 0 ? 3 : 2, tw: i % 4 === 0, delay: (i % 11) * 0.45 };
});

/** The hour's light on a staged backdrop (docs/22 O3), the same four phases the board uses. */
export type DayPhase = 'night' | 'dawn' | 'day' | 'dusk';
export const dayPhase = (hour: number): DayPhase => (hour >= 21 || hour < 5 ? 'night' : hour < 8 ? 'dawn' : hour < 17 ? 'day' : 'dusk');
const PHASE_LOOK: Record<DayPhase, Pick<SceneDef, 'tint' | 'stars' | 'moon' | 'sun'>> = {
  night: { tint: '#5f6fb8', stars: 80, moon: true },
  dawn: { tint: '#ffd2b4', sun: true },
  day: { tint: undefined },
  dusk: { tint: '#e8a07c', stars: 12 },
};

/**
 * The scene re-lit for a staged battle (docs/22 O3): the story's ground drawn in its region kit, the
 * sky and the tint of its hour, and its weather over the top. Clouded skies hide the stars and sun.
 */
/** Skies with no cloud in them: the stars, the moon and the sun all show (docs/23: heat and frost are clear and still). */
const OPEN_SKY: readonly string[] = ['Clear', 'Heat', 'Frost'];
/** Weathers that hide the sun entirely. */
const NO_SUN: readonly string[] = ['Storm', 'Snow', 'Blizzard', 'Sandstorm', 'Sleet'];

/**
 * A staged backdrop: the story's stage, or a fight's own (`encounter_stage`, the epilogue): then
 * `kit` is the board's ground kit itself, which wins over the story's `ground`.
 */
export type BackdropStage = Omit<Stage, 'ground'> & { ground?: Ground; kit?: string };

/** The hour as it looks in a season (docs/23: winter days are short, summer evenings long), as the board reads it. */
const litPhase = (hour: number, season: StorySeason | undefined): DayPhase => dayPhase(season ? seasonHour(hour, season.toLowerCase() as Season) : hour);

function staged(def: SceneDef, stage: BackdropStage, season?: StorySeason): SceneDef {
  const look = PHASE_LOOK[litPhase(stage.hour, season)];
  const clouded = !OPEN_SKY.includes(stage.weather);
  return { ...def, kit: stage.kit || (stage.ground && GROUND_KIT[stage.ground]) || def.kit, tint: look.tint, stars: clouded ? 0 : look.stars, moon: !clouded && look.moon, sun: !NO_SUN.includes(stage.weather) && look.sun };
}

/** Skies quiet enough for the season's dressing to show over a staged scene (the weather wins where they clash). */
const CALM_SKY: readonly string[] = ['Clear', 'Overcast', 'Fog'];

/**
 * Review hook (story harness only): `?intro=1&step=99&stage=Heat,Dunes,13` stages the call to arms
 * on any weather, ground and hour, so every docs/23 backdrop can be checked without a server.
 */
function stageOverride(): Stage | undefined {
  try {
    const q = new URLSearchParams(window.location.search);
    const v = q.get('stage');
    if (!q.get('intro') || !v) return undefined;
    const [weather, ground, hour] = v.split(',');
    return { weather: (weather || 'Clear') as StoryWeather, ground: (ground || 'Greenwood') as Ground, hour: Number.isFinite(Number(hour)) && hour !== '' ? Number(hour) : 12 };
  } catch { return undefined; }
}

/**
 * The region kit a story panel is painted in (docs/23): the realm's country in the story's season,
 * as on the overworld chart (`overworldKit`). A green temperate story keeps each scene's authored
 * kit (the marsh, the burning autumn wood); an autumn one turns every wood but the marsh.
 */
export function regionKit(sceneKit: string, biome: StoryBiome | undefined, season: StorySeason | undefined): string {
  if (!biome && !season) return sceneKit;
  const k = overworldKit(biome?.toLowerCase(), season?.toLowerCase() as SeasonKey | undefined);
  if (k === 'forest') return sceneKit;
  if (k === 'autumn') return sceneKit === 'mirewood' ? 'mirewood' : 'autumn';
  return k;
}

/** The seasonal dressing over an unstaged panel (docs/23): snow in winter, leaves in autumn, blossom in spring — where the country has them. */
export function seasonDressing(biome: StoryBiome | undefined, season: StorySeason | undefined): 'snow' | 'leaves' | 'petals' | undefined {
  if (!season || biome === 'Jungle' || biome === 'Underdark' || biome === 'Ashlands') return undefined;
  if (season === 'Winter') return biome === 'Desert' ? undefined : 'snow';
  if (season === 'Autumn') return biome === 'Desert' ? undefined : 'leaves';
  if (season === 'Spring') return biome === 'Desert' ? undefined : 'petals';
  return undefined;
}

/** Review hook (story harness only): `?intro=1&season=Winter&biome=Desert` paints every panel in that calendar and country. */
function storyOverride(): { season?: StorySeason; biome?: StoryBiome } {
  try {
    const q = new URLSearchParams(window.location.search);
    if (!q.get('intro')) return {};
    return { season: (q.get('season') as StorySeason | null) ?? undefined, biome: (q.get('biome') as StoryBiome | null) ?? undefined };
  } catch { return {}; }
}

/** Deterministic falling particles for the CSS weather layers (snow, blizzard, sleet). */
const flakes = (n: number, seed: number, size: [number, number], dur: [number, number]) => Array.from({ length: n }, (_, i) => {
  const a = hash01(seed * 131 + i), b = hash01(seed * 131 + i + 7919);
  const s = i % 5 === 0 ? size[1] : size[0];
  return { left: `${(a * 112 - 6).toFixed(2)}%`, animationDelay: `${(-b * dur[1]).toFixed(2)}s`, animationDuration: `${(dur[0] + ((i * 7) % 10) / 10 * (dur[1] - dur[0])).toFixed(2)}s`, width: s, height: s } as CSSProperties;
});

/**
 * One full-window backdrop. `figures` stand on the ground (the call to arms shows the heroes). With a
 * `stage` the scene is re-lit for the first battle: its ground, its hour and its weather. Without
 * one, `season` and `biome` (the story's calendar and realm, docs/23) pick the panel's region kit and
 * a light seasonal dressing (snow, falling leaves, blossom).
 */
export function SceneBackdrop({ scene, figures, stage: given, season, biome, cast, seed = 0, onPainted, className = '' }: {
  scene: Scene; figures?: React.ReactNode; stage?: BackdropStage; season?: StorySeason; biome?: StoryBiome;
  /** the panel's cast from the story lexicon; `seed` places it */ cast?: Cast; seed?: number;
  /** fires after every paint of the canvas (the intro lifts its curtain on the first) */ onPainted?: () => void; className?: string;
}) {
  const pinned = useMemo(() => stageOverride(), []);
  const stage: BackdropStage | undefined = given && pinned ? pinned : given;
  const story = useMemo(() => storyOverride(), []);
  const s = story.season ?? season, b = story.biome ?? biome;
  const deep = b === 'Underdark' || stage?.ground === 'Underdark';
  const ash = !deep && (b === 'Ashlands' || stage?.ground === 'Ashlands');
  const base = SCENES[scene] ?? SCENES.Night;
  const acting = cast && !emptyCast(cast) ? cast : undefined;
  const blocking = useMemo(() => (acting ? blockScene(acting, base.rows.map(pad), seed + base.seed, { hasFills: !!base.fills }) : undefined), [acting, base, seed]);
  const def = useMemo(() => {
    let d: SceneDef = base;
    if (blocking && acting) {
      // the cast's cells clear the authored props under them; a road is dirt laid under everything
      const keep = (p: Prop) => !blocking.taken.has(`${p.x},${p.y}`) && blocking.rows[p.y]?.[p.x] !== '~';
      d = {
        ...base, kit: acting.kit ?? base.kit, rows: blocking.rows, placed: blocking.placed,
        props: (base.props ?? []).filter(keep), fills: [...(base.fills ?? []).filter(keep), ...blocking.road.map((c) => ({ frame: FILLS.dirt, ...c }))],
        moon: base.moon || acting.sky.moon !== undefined, sun: (base.sun || !!acting.sky.sun) && !acting.sky.storm, stars: acting.sky.stars ? Math.max(base.stars ?? 0, 90) : base.stars,
      };
    }
    const lit = stage ? staged(d, stage, s) : { ...d, kit: regionKit(d.kit, b, s) };
    // docs/23 Y9: under the rock there are no stars, no moon and no sun, only the cave roof's dark;
    // Y10: no star or moon shows through the Ashlands' pall (the sun is a copper coin, intro.css)
    return deep ? { ...lit, stars: 0, moon: false, sun: false } : ash ? { ...lit, stars: 0, moon: false } : lit;
  }, [base, blocking, acting, stage, b, s, deep, ash]);
  // a staged scene keeps its own weather; under a quiet sky the season dresses it too (no snow on the dunes)
  const dressing = stage ? (CALM_SKY.includes(stage.weather) && !/dunes|desert/.test(def.kit) ? seasonDressing(b, s) : undefined) : seasonDressing(b, s);
  const { ref, w, h, ready } = useSceneCanvas(def, onPainted);
  const stars = useMemo(() => starField(def.stars ?? 0, def.seed), [def]);
  const reduced = useReducedMotion();
  const weather = stage?.weather;
  const shimmer = weather === 'Heat' && !reduced;
  const look = stage ? ` fk-scene--staged fk-scene--hour-${litPhase(stage.hour, s)} fk-scene--sky-${stage.weather.toLowerCase()}` : '';
  // the sky the words name, over an unstaged panel (a staged one keeps the battle's own weather)
  const sky = !stage ? acting?.sky ?? {} : {};
  const rain = weather === undefined && (scene === 'Storm' || !!sky.rain || !!sky.storm);
  const flash = weather === undefined && (scene === 'Storm' || !!sky.storm);
  const mist = weather === undefined && (scene === 'Marsh' || !!sky.mist);
  // the Ashlands' air is always full of embers and drifting ash
  const embers = scene === 'Fire' || ash || (!stage && !!sky.embers);
  const snowing = !stage && !!sky.snow && dressing !== 'snow';
  /** a cell's centre on the window: the strip is centred and stands on the bottom edge */
  const cellAt = (x: number, y: number): CSSProperties => ({ left: `calc(50% + ${(x + 0.5 - w / 2) * CELL * SCALE}px)`, bottom: (h - y - 0.5) * CELL * SCALE });
  return (
    <div className={`fk-scene fk-scene--${scene.toLowerCase()}${look}${sky.moon === 'red' ? ' fk-scene--red-moon' : ''}${deep ? ' fk-scene--underdark' : ''}${ash ? ' fk-scene--ashlands' : ''} ${className}`} aria-hidden="true" data-scene={scene} data-ready={ready ? 'true' : 'false'} data-weather={weather} data-kit={def.kit} data-season={s} data-dressing={dressing} data-cues={acting?.cues.join(' ')}>
      <div className="fk-scene__sky">
        {stars.map((s, i) => <span key={i} className={`fk-scene__star ${s.tw ? 'fk-scene__star--tw' : ''}`} style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }} />)}
        {def.moon && <span className="fk-scene__moon" />}
        {def.sun && <span className="fk-scene__sun" />}
        {scene === 'Stars' && !reduced && <span className="fk-scene__falling" />}
        <span className="fk-scene__clouds" />
      </div>
      {/* the land: the weather's filter (heat shimmer, frost, sand, whiteout) sits on this wrapper so the hour's filter stays on the canvas */}
      <div className={`fk-scene__land${shimmer ? ' fk-scene__land--shimmer' : ''}`}>
        <canvas ref={ref} className="fk-scene__ground" width={w * CELL} height={(h + HEAD) * CELL} style={{ width: w * CELL * SCALE, height: (h + HEAD) * CELL * SCALE } as CSSProperties} />
        {blocking?.glows.map((g, i) => <span key={i} className={`fk-scene__light fk-scene__light--${g.kind}`} style={cellAt(g.x, g.y)} />)}
      </div>
      {shimmer && (
        <svg className="fk-scene__defs" width="0" height="0" aria-hidden="true" focusable="false">
          {/* heat shimmer: thin horizontal noise bands, gently re-rolled, nudge the painted ground sideways by a pixel or two */}
          <filter id="fk-scene-heat" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.004 0.09" numOctaves="1" seed="4" result="n">
              <animate attributeName="baseFrequency" dur="2.8s" values="0.004 0.09;0.005 0.075;0.004 0.09" repeatCount="indefinite" />
            </feTurbulence>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="7" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </svg>
      )}
      {figures && <div className="fk-scene__figures">{figures}</div>}
      {rain && <span className="fk-scene__rain" />}
      {flash && <span className="fk-scene__flash" />}
      {mist && <span className="fk-scene__mist" />}
      {snowing && <div className="fk-scene__snow fk-scene__snow--light">{flakes(40, 23, [3, 4], [8, 12]).map((st, i) => <span key={i} style={st} />)}</div>}
      {(weather === 'Rain' || weather === 'Storm') && <span className="fk-scene__rain" />}
      {weather === 'Storm' && <span className="fk-scene__flash" />}
      {weather === 'Fog' && <><span className="fk-scene__mist" /><span className="fk-scene__fog" /></>}
      {weather === 'Snow' && <div className="fk-scene__snow">{Array.from({ length: 70 }, (_, i) => <span key={i} style={{ left: `${(i * 53) % 100}%`, animationDelay: `${(i * 0.37) % 9}s`, animationDuration: `${7 + (i % 6)}s`, width: i % 5 === 0 ? 4 : 3, height: i % 5 === 0 ? 4 : 3 }} />)}</div>}
      {dressing === 'snow' && <div className="fk-scene__snow fk-scene__snow--light">{flakes(34, 11, [3, 4], [8, 12]).map((st, i) => <span key={i} style={st} />)}</div>}
      {dressing === 'leaves' && <div className="fk-scene__fall fk-scene__fall--leaves">{flakes(22, 13, [4, 5], [8, 13]).map((st, i) => <span key={i} style={st} />)}</div>}
      {dressing === 'petals' && <div className="fk-scene__fall fk-scene__fall--petals">{flakes(28, 17, [3, 4], [9, 14]).map((st, i) => <span key={i} style={st} />)}</div>}
      {weather === 'Heat' && <><span className="fk-scene__heat" /><span className="fk-scene__haze fk-scene__haze--heat" /></>}
      {weather === 'Sandstorm' && <><span className="fk-scene__dust" /><span className="fk-scene__sand" /><span className="fk-scene__sand fk-scene__sand--far" /></>}
      {weather === 'Blizzard' && <><span className="fk-scene__whiteout" /><div className="fk-scene__blizzard">{flakes(150, 3, [3, 5], [1.6, 3.2]).map((st, i) => <span key={i} style={st} />)}</div></>}
      {weather === 'Frost' && <><span className="fk-scene__frost" /><div className="fk-scene__glints">{flakes(26, 9, [2, 3], [3, 6]).map((st, i) => <span key={i} style={{ ...st, top: `${(55 + hash01(i * 17 + 5) * 40).toFixed(1)}%` }} />)}</div></>}
      {weather === 'Sleet' && <><span className="fk-scene__rain fk-scene__rain--sleet" /><div className="fk-scene__snow fk-scene__snow--sleet">{flakes(28, 5, [3, 3], [3.2, 5]).map((st, i) => <span key={i} style={st} />)}</div></>}
      {embers && <><span className="fk-scene__glow" /><div className="fk-scene__embers">{Array.from({ length: 22 }, (_, i) => <span key={i} className="fk-scene__ember" style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i * 0.53) % 7}s`, animationDuration: `${5 + (i % 5)}s` }} />)}</div></>}
      {scene === 'Dawn' && <span className="fk-scene__haze" />}
      <span className="fk-scene__veil" />
    </div>
  );
}

export const SCENE_NAMES = Object.keys(SCENES) as Scene[];
